import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { supabase } from '../../config/supabase';
import { useOrganization } from '../../contexts/OrganizationContext';
import logger from '../../utils/logger';

export default function CallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentOrganization, loading: orgLoading } = useOrganization();
  const [error, setError] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);

  // Get redirect URL from query params
  const redirectUrl = searchParams.get('redirect');

  // First effect: Handle auth session (OAuth or email confirmation)
  useEffect(() => {
    let resolved = false;

    const resolve = () => {
      resolved = true;
      setSessionReady(true);
    };

    // Listen for the SIGNED_IN event — covers PKCE code exchange completing async
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && !resolved) {
        resolve();
      }
    });

    // Also check if a session is already available (covers fast exchange / existing session)
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('Error during auth callback:', error);
        subscription.unsubscribe();
        setError(error.message);
        navigate('/login', {
          replace: true,
          state: { error: 'Sign-in failed. Please try again.' },
        });
        return;
      }
      if (session && !resolved) {
        resolve();
      }
    });

    // Timeout: if no session arrives within 15 seconds, redirect to login
    const timeout = setTimeout(() => {
      if (!resolved) {
        console.error('Auth callback timed out — no session established');
        subscription.unsubscribe();
        navigate('/login', {
          replace: true,
          state: { error: 'Sign-in timed out. Please try again.' },
        });
      }
    }, 15000);

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Second effect: Navigate after organization is loaded
  useEffect(() => {
    if (!sessionReady || orgLoading) {
      logger.debug('[CallbackPage] Waiting... sessionReady:', sessionReady, 'orgLoading:', orgLoading);
      return; // Wait for session and organization to be ready
    }

    logger.debug('[CallbackPage] Session and org ready, currentOrganization:', currentOrganization?.id);

    // Small delay to ensure all contexts are fully initialized
    const timer = setTimeout(() => {
      // First check if there's a redirect URL from OAuth callback
      if (redirectUrl) {
        logger.debug(`[CallbackPage] Found OAuth redirect, navigating to: ${redirectUrl}`);
        navigate(redirectUrl, { replace: true });
        return;
      }
      
      // Then check if there's a login redirect (for invitations stored before OAuth)
      const loginRedirect = localStorage.getItem('loginRedirect');
      if (loginRedirect) {
        logger.debug(`[CallbackPage] Found login redirect, navigating to: ${loginRedirect}`);
        localStorage.removeItem('loginRedirect'); // Clean up
        navigate(loginRedirect, { replace: true });
        return;
      }

      // Try to get the last active project for the current organization
      let lastProjectId = null;
      
      if (currentOrganization) {
        // Try org-scoped key first
        const orgKey = `activeProjectId:${currentOrganization.id}`;
        lastProjectId = localStorage.getItem(orgKey);
        logger.debug(`[CallbackPage] Checking org-scoped key: ${orgKey} = ${lastProjectId}`);
      }
      
      // Fallback to legacy key if no org-scoped key found
      if (!lastProjectId) {
        lastProjectId = localStorage.getItem('activeProjectId');
        logger.debug(`[CallbackPage] Fallback to legacy key: activeProjectId = ${lastProjectId}`);
      }

      logger.debug('[CallbackPage] All localStorage keys:', Object.keys(localStorage).filter(k => k.includes('Project')));

      if (lastProjectId) {
        logger.debug(`[CallbackPage] Navigating to project: ${lastProjectId}`);
        navigate(`/projects/${lastProjectId}`, { replace: true });
      } else {
        logger.debug('[CallbackPage] No saved project, navigating to home');
        navigate('/', { replace: true });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [sessionReady, orgLoading, currentOrganization, navigate, redirectUrl]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      textAlign="center"
      p={3}
    >
      {error ? (
        <>
          <Typography color="error" variant="h6" gutterBottom>
            Sign-in Error
          </Typography>
          <Typography color="textSecondary">
            {error}
          </Typography>
        </>
      ) : (
        <>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {!sessionReady ? 'Signing you in...' : 'Loading your workspace...'}
          </Typography>
        </>
      )}
    </Box>
  );
}
